import { inject, Injectable } from "@angular/core";
import { first, map } from "rxjs";
import { GraphService } from "@kernel/graphql/services/graph.service";
import { GraphqlMessageInterface } from "@app/kernel/graphql/interfaces/graphql-message.interface";
import { IReservationSourceParent } from "../interfaces/reservation-source-parent.interface";
import { RESERVATION_SOURCE_PARENT_FORM_QUERY } from "../queries/reservation-source-parent-form.query";

/**
 * GraphQL operations for reservation source parents. Provided on the domain route, not in root.
 * Create and update go through the form mutation configs.
 */
@Injectable()
export class ReservationSourceParentApiService {
  /** GraphQL service used for executing queries and mutations. */
  private readonly graphService = inject(GraphService);

  /**
   * Fetches a reservation source parent by id or entityId.
   *
   * @param id - The reservation source parent id or entityId.
   * @param dataShape - Selection set; defaults to the form query.
   * @returns An observable that emits the reservation source parent.
   * @throws GraphQL error if the response contains errors.
   */
  getReservationSourceParent(id: string, dataShape: string | string[] = RESERVATION_SOURCE_PARENT_FORM_QUERY) {
    return this.graphService
      .constructQuery<{ getReservationSourceParent: IReservationSourceParent }>(dataShape, "getReservationSourceParent", { id })
      .pipe(
        first(),
        map(data => {
          if (data.errors?.length) {
            throw data.errors[0];
          }
          return data.data.getReservationSourceParent;
        })
      );
  }

  /**
   * Enables a reservation source parent.
   *
   * @param id - The reservation source parent id.
   * @param dataShape - Selection set; defaults to the form query.
   * @returns An observable that emits the enabled reservation source parent.
   * @throws GraphQL error if the response contains errors.
   */
  enableReservationSourceParent(id: string, dataShape: string | string[] = RESERVATION_SOURCE_PARENT_FORM_QUERY) {
    const shape = Array.isArray(dataShape) ? dataShape : [dataShape];
    return this.graphService
      .constructMutation<{ enableReservationSourceParent: IReservationSourceParent }>("enableReservationSourceParent", { id: "ID!" }, { id }, shape)
      .pipe(
        first(),
        map(data => {
          if (data.errors?.length) {
            throw data.errors[0];
          }
          return data.data?.enableReservationSourceParent;
        })
      );
  }

  /**
   * Disables a reservation source parent.
   *
   * @param id - The reservation source parent id.
   * @param dataShape - Selection set; defaults to the form query.
   * @returns An observable that emits the disabled reservation source parent.
   * @throws GraphQL error if the response contains errors.
   */
  disableReservationSourceParent(id: string, dataShape: string | string[] = RESERVATION_SOURCE_PARENT_FORM_QUERY) {
    const shape = Array.isArray(dataShape) ? dataShape : [dataShape];
    return this.graphService
      .constructMutation<{ disableReservationSourceParent: IReservationSourceParent }>("disableReservationSourceParent", { id: "ID!" }, { id }, shape)
      .pipe(
        first(),
        map(data => {
          if (data.errors?.length) {
            throw data.errors[0];
          }
          return data.data?.disableReservationSourceParent;
        })
      );
  }

  /**
   * Soft-deletes a reservation source parent.
   *
   * @param id - The reservation source parent id.
   * @returns An observable that emits the backend message.
   * @throws GraphQL error if the response contains errors.
   */
  deleteReservationSourceParent(id: string) {
    return this.graphService
      .constructMutation<{
        deleteReservationSourceParent: GraphqlMessageInterface;
      }>("deleteReservationSourceParent", { id: "ID!" }, { id }, ["text", "code"])
      .pipe(
        first(),
        map(data => {
          if (data.errors?.length) {
            throw data.errors[0];
          }
          return data.data?.deleteReservationSourceParent;
        })
      );
  }
}
